/**
 * Icon wrapper (UID-27, docs/design/assets-brand.md "Iconography" +
 * open-questions.md #2).
 *
 * DECISION RECORDED (open-questions.md #2, resolved): the icon library is
 * `@ant-design/icons`, imported per-icon (tree-shakeable ES imports) for
 * fidelity against the hi-fi mock, per the ticket's explicit instruction —
 * see `package.json`'s dependency entry.
 *
 * The dependency is now installed (lockfile committed), so every icon below
 * is a direct re-export of the real `@ant-design/icons` component — no more
 * hand-authored inline-SVG stand-ins. Every exported name and the call-site
 * API (size via inherited `font-size`/explicit `style.fontSize`, color via
 * `currentColor`, `aria-hidden`/`className` passthrough) is unchanged, so no
 * consumer (`Sidebar.tsx`, `TopBar.tsx`, `HomePage.tsx`) needed to change.
 * `@ant-design/icons` renders each icon as `<span role="img"><svg .../></span>`
 * with the inner `<svg>` sized `1em`/`1em` and filled `currentColor` by
 * default — the same "inherit font-size, inherit text color" contract the
 * previous inline-SVG stand-ins implemented by hand.
 */
export {
  AppstoreOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  BankOutlined,
  BellOutlined,
  BgColorsOutlined,
  BuildOutlined,
  BulbOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  CodeSandboxOutlined,
  CopyOutlined,
  CreditCardOutlined,
  CustomerServiceOutlined,
  DeleteOutlined,
  DisconnectOutlined,
  DownOutlined,
  DownloadOutlined,
  EditOutlined,
  EllipsisOutlined,
  ExclamationCircleOutlined,
  ExperimentOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FileOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  FilterOutlined,
  FolderOutlined,
  HeartOutlined,
  HomeOutlined,
  LeftOutlined,
  LoadingOutlined,
  LockOutlined,
  MailOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PauseCircleOutlined,
  PictureOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  PushpinOutlined,
  QuestionCircleOutlined,
  RedoOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  SearchOutlined,
  SettingOutlined,
  ShareAltOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  TableOutlined,
  TagOutlined,
  ThunderboltOutlined,
  TruckOutlined,
  UploadOutlined,
  UsergroupAddOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons'

import type { CSSProperties, ReactNode } from 'react'

/**
 * 24x24px minimum-hit-target wrapper for a bare decorative/standalone icon
 * (components.md "Icon button" — 24x24px min hit target even at a 14px
 * icon size). Use around any icon that is itself the clickable surface;
 * icons inline in text/buttons that already have their own padding don't
 * need this.
 */
export function IconTargetBox({
  children,
  size = 24,
  style,
}: {
  readonly children: ReactNode
  readonly size?: number
  readonly style?: CSSProperties
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: Math.max(size, 24),
        height: Math.max(size, 24),
        flexShrink: 0,
        ...style,
      }}
    >
      {children}
    </span>
  )
}

/**
 * UID-130 — cart ship-plan tab icons, matching the design comp exactly
 * (`Fictiv Platform.dc.html`, ship-plan tabs). `@ant-design/icons` has no
 * equal, so these are the prototype's own inline SVGs (lucide-style, 19px,
 * 1.9 stroke, currentColor). Decorative — always `aria-hidden` (the tab text
 * label carries the meaning).
 */
export function IconShipTogether({ size = 18 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="m7.5 4.27 9 5.15" />
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </svg>
  )
}

export function IconShipAsReady({ size = 18 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <rect x="2" y="3" width="9" height="8" rx="1.5" />
      <rect x="13" y="13" width="9" height="8" rx="1.5" />
      <path d="M6.5 11v3a2 2 0 0 0 2 2h4.5" />
    </svg>
  )
}

/**
 * UID-131 — "Send to PunchOut" button icon, matching the prototype exactly
 * (`Fictiv Platform.dc.html:1925` — a POS/card-terminal glyph: card body,
 * top card-slot tab, and center keypad marks). `@ant-design/icons` has no
 * equal (its terminal-adjacent icons are wallet/credit-card shapes, not this
 * silhouette), so this is the prototype's own inline SVG, same
 * lucide-style/16px/2px-stroke/currentColor convention as the ship-plan tab
 * icons above. Decorative — always `aria-hidden` (the button label carries
 * the meaning).
 */
export function IconPunchOutTerminal({ size = 16 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
      <path d="M12 12v4M10 14h4" />
    </svg>
  )
}

/**
 * UID-248 — the Materials.AI chat modal's header spark glyph (`Fictiv
 * Platform.dc.html:4007`, a large 4-point spark + a smaller offset spark).
 * `@ant-design/icons` has no equal (its AI-adjacent icons — `BulbOutlined`,
 * `ThunderboltOutlined` — are a different metaphor), so this is the
 * prototype's own inline SVG, verbatim path data, filled `currentColor` (not
 * stroked, matching the prototype's `fill="currentColor"` usage). Decorative
 * alongside the modal's own visible "Materials.AI" title — always
 * `aria-hidden`. */
export function IconAiSpark({ size = 18 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M12 2l1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6L12 2z" />
      <path d="M19 14l.7 2.3L22 17l-2.3.7L19 20l-.7-2.3L16 17l2.3-.7L19 14z" />
    </svg>
  )
}

/**
 * UID-225 (Parts Library visual-fidelity pass) — the "Custom" source chip's
 * sun/asterisk glyph (`Fictiv Platform.dc.html` ~L1308, `it.isCustom`
 * branch), verbatim path data from the prototype. `@ant-design/icons`'s
 * `SunOutlined` uses a differently-proportioned ray pattern, so — same
 * "prototype has no ant-design equivalent" rationale as `IconShipTogether`/
 * `IconShipAsReady` above — this is hand-inlined at the prototype's own
 * 12px/2px-stroke sizing rather than approximated. Decorative — always
 * `aria-hidden` (the chip's own text label, `FILTER_STATUS_LABEL`-adjacent
 * "Custom"/"Standard" text, carries the meaning per WCAG 1.4.1).
 */
export function IconCustomSparkle({ size = 12 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" />
    </svg>
  )
}

/**
 * UID-225 — the "Standard" source chip's package/box glyph (`Fictiv
 * Platform.dc.html` ~L1308, `it.isStandard` branch), verbatim path data.
 * `@ant-design/icons`'s box-shaped icons (`BoxPlotOutlined` etc.) are chart
 * glyphs, not this open-package silhouette — hand-inlined for the same
 * reason as `IconCustomSparkle` above. Decorative (`aria-hidden`).
 */
export function IconStandardBox({ size = 12 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5M12 22V12" />
    </svg>
  )
}

/**
 * UID-225 Round 2 (GAP-R2-1) — the parts-row reorder/quick-add-to-cart
 * button's "+" glyph. The design of record renders a true inline SVG
 * (`viewBox 0 0 24 24`, `d="M12 5v14M5 12h14"`, round caps/joins,
 * `stroke-width: 2.2`) — this repo's build previously rendered a literal
 * text `+` character (font-size/weight-dependent, drifts across
 * browsers/OS font rendering). The design decision was to match with
 * `lucide-react`'s `Plus` icon, but `lucide-react` is not an installed
 * dependency of this workspace and adding one requires a lockfile change
 * this sandbox cannot regenerate (no npm-registry network — see
 * `monorepo-layout.md`). This is `lucide-react`'s own `Plus` path data,
 * hand-inlined at the exact spec geometry, following the same
 * "prototype/lucide-style inline SVG" convention already used above for
 * `IconShipTogether`/`IconPunchOutTerminal`/etc. Decorative when paired
 * with the button's own `aria-label` — always `aria-hidden`.
 */
export function IconPlus({
  size = 18,
  strokeWidth = 2.2,
}: {
  readonly size?: number
  readonly strokeWidth?: number
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

/**
 * The "Choose files or BOM" CTA's leading upload glyph (tray + up-arrow).
 * Path data lifted verbatim from the design export's Choose-files button
 * (`Fictiv Platform.dc.html:455`): the Lucide "upload" icon at 16px /
 * stroke 2.2. Decorative alongside the button's text label — `aria-hidden`.
 */
export function IconUpload({
  size = 16,
  strokeWidth = 2.2,
}: {
  readonly size?: number
  readonly strokeWidth?: number
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  )
}

/**
 * UID-260 (ADR-0085) — the "Configure" CTA's wrench glyph, both per-row
 * (`PartsReviewTable`) and the summary-rail's needs-config CTA
 * (`RequestSummaryRail`). Exact path data lifted verbatim from the design
 * export (`Fictiv Platform.dc.html` ~L1343/L4226's `it.notCart`/library
 * bulk-configure button), the design's own literal Configure wrench —
 * not an ant-design/lucide substitute, since neither ships a pixel match
 * for this exact glyph. Decorative alongside the button's own text label —
 * always `aria-hidden`. */
export function IconWrench({ size = 14 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  )
}

/**
 * UID-260 gap #4 follow-up — the per-row "Volume" link's small line-chart
 * glyph (`PartsReviewTable.tsx`). Same path data as
 * `verticals/parts/components/VolumePricingPopover.tsx`'s inline
 * `VolumeCurveMiniIcon` — hoisted here so the two verticals share one glyph
 * instead of two copies drifting apart. Decorative alongside the trigger's
 * own visible "Volume" text label — always `aria-hidden`. */
export function IconLineChart({ size = 12 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M3 3v18h18" />
      <path d="M19 9c-2.5 0-3.5 6-7 6s-4.5-4-9-3.5" />
    </svg>
  )
}

/**
 * UID-260 — the submit CTA's paper-plane glyph once every line is priced
 * (design fact #4, this ticket's EM decision). Path data lifted verbatim
 * from the design export's `rfqDraftView.onSubmitCustom` button
 * (`Fictiv Platform.dc.html` ~L4366). Decorative alongside the button's own
 * text label — always `aria-hidden`. */
export function IconPaperPlane({ size = 16 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </svg>
  )
}

/**
 * Outline clock glyph for the Generate-quote modal's "locked for 30 days" note,
 * verbatim path data from the prototype (`Fictiv Platform.dc.html:4989`) — a
 * plain circle + clock hands, matching this file's other hand-authored
 * lucide-style icons (`IconPaperPlane` etc.) rather than antd's
 * `ClockCircleOutlined` (a visually different stroke weight/proportions,
 * introducing icon-language inconsistency alongside them). Decorative
 * alongside the note's own text — always `aria-hidden`. */
export function IconClock({ size = 14 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  )
}

/**
 * UID-427 — chain-link glyph for the quote-generated modal's "Copy shareable
 * link" action, matching this file's other hand-authored lucide-style icons
 * (`IconPaperPlane`, `IconClock`). Decorative alongside the button's own text
 * label — always `aria-hidden`. */
export function IconLink({ size = 16 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  )
}

/**
 * UID-430 (Generate-quote confirm modal design-fidelity fix) — map-pin glyph
 * for the ship-to card's 32×32 avatar (`Fictiv Platform.dc.html:4977`, a
 * standard lucide "map-pin" teardrop + inner circle). No pin/map-marker
 * glyph existed in this file before; matching this file's other
 * hand-authored lucide-style icons (`IconLink`/`IconClock`/`IconPaperPlane`)
 * rather than reaching for an antd equivalent. Decorative alongside the
 * ship-to name/address text next to it — always `aria-hidden`. */
export function IconMapPin({ size = 17 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  )
}

/**
 * UID-430 — thin-stroke checkmark for the quote-generated (ready) state's
 * success icon (`Fictiv Platform.dc.html:5019`), replacing antd's solid-glyph
 * `CheckOutlined` there. `strokeWidth="2.4"` (not this file's usual `"2"`)
 * matches the design's own specific stroke weight for this glyph. Decorative
 * alongside the "Quote generated" heading right next to it — always
 * `aria-hidden`. Scoped to that one icon slot — the OTHER `CheckOutlined`
 * usage in `GenerateQuoteModal.tsx` (the "Copied!" button state, PR #384) is
 * unrelated and intentionally left as antd's icon. */
export function IconCheck({ size = 30 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

/**
 * UID-263 (ADR-0087 D4, UX spec §2/§6 item 3) — the summary rail's bulk
 * "Add N priced to cart" button's cart-glyph (`Fictiv Platform.dc.html:4371`,
 * 16x16, stroke-width 2.2). `@ant-design/icons`'s `ShoppingCartOutlined` is a
 * filled/solid glyph, not the design's stroke-outline silhouette, so — same
 * "prototype has no ant-design equivalent" rationale as `IconShipTogether`/
 * `IconPunchOutTerminal`/`IconPlus` above — this is a lucide-style inline SVG
 * at the design's own sizing. Decorative alongside the button's own visible
 * "Add N priced to cart" text label — always `aria-hidden`. */
export function IconCartGlyph({ size = 16 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  )
}

/**
 * UID-263 (ADR-0087 D4, UX spec §3/§6 item 4) — the bulk-add confirm modal's
 * amber warning-triangle icon chip (`Fictiv Platform.dc.html:4557-4567`,
 * 20x20, stroke-width 2). `@ant-design/icons`'s `WarningOutlined``/`
 * `ExclamationCircleOutlined` are filled/circle glyphs, not this design's
 * outline triangle — same rationale as `IconCartGlyph` above. Decorative
 * alongside the modal's own visible heading text — always `aria-hidden`
 * (the chip's parent supplies the icon-only presentational wrapper). */
export function IconWarningTriangle({ size = 20 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  )
}

/**
 * UID-295/296 (BOMs design-alignment) — the BOM detail parts-table's
 * assembly/sub-assembly row "layers" glyph (measured spec §B4: `polygon
 * points="12 2 2 7 12 12 22 7 12 2"` + two chevron polylines, 15×15px).
 * `@ant-design/icons` has no literal "layers" icon — the spec's own flag
 * ("may need a bespoke icon component") — so this is the prototype's exact
 * polygon/polyline geometry, hand-inlined at the measured size. Decorative
 * alongside the row's own visible assembly name — always `aria-hidden`.
 */
export function IconLayers({ size = 15 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  )
}

/**
 * UID-295/296 — the BOM detail parts-table leaf row's drag-handle glyph
 * (measured spec §B4: a 6-dot grip, `fill="currentColor"`, 14×14px).
 * `@ant-design/icons`'s closest equivalent (`HolderOutlined`) has a
 * differently-spaced dot grid — the spec flags this as an uncertain glyph
 * match, so this is a standard 2×3-dot grip, hand-inlined rather than
 * approximated. Decorative — paired with the row's own keyboard-operable
 * Move up/down buttons (accessibility-wcag: drag alone must not be the only
 * reorder mechanism) — always `aria-hidden`.
 */
export function IconDragHandle({ size = 14 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <circle cx="8" cy="5" r="1.75" />
      <circle cx="8" cy="12" r="1.75" />
      <circle cx="8" cy="19" r="1.75" />
      <circle cx="16" cy="5" r="1.75" />
      <circle cx="16" cy="12" r="1.75" />
      <circle cx="16" cy="19" r="1.75" />
    </svg>
  )
}

/**
 * UID-253 catalog rebuild — the "Shop by category" landing's group-tile icon
 * chip (`Fictiv Platform.dc.html:868`, a 4-rounded-square grid glyph, one per
 * group, tinted per `.catalog-group-tint-*` in `catalog.css`). Same glyph for
 * every group — the mock varies only the tint, not the icon — so this is the
 * one shared decorative icon; always paired with the group's own visible
 * name/subcount text, so `aria-hidden` here loses no information. */
export function IconCategoryGrid({ size = 19 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
    </svg>
  )
}

/** "Copy from Excel" button icon — the Lucide "copy" glyph (two overlapping
 * rounded squares) the design uses (`Fictiv Platform.dc.html`'s `onOpenXl`
 * button, both the Cart header action and Home's upload-card footer, UID-293
 * matching Cart's own icon exactly rather than re-authoring a lookalike). */
export function ExcelCopyIcon({ size = 16 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  )
}

/**
 * UID-367 — the tinted "project/workspace" folder-tile glyph. The design
 * source reuses this EXACT path data (open folder, `rect x=3 y=7 w=18 h=13
 * rx=2` + a small corner-tab path) at several sizes/contexts — the Sidebar
 * "Projects" mini-list row (`Fictiv Platform.dc.html:252`), the library
 * "Add to project" menu row (~L1330), a workspace header's icon chip
 * (~L2565), and the upload "Add these files to a project" modal (~L4807) —
 * so, like `IconStandardBox`/`IconLayers` above, it earns one shared,
 * reusable glyph rather than four inline copies. `@ant-design/icons`'s
 * `FolderOutlined` (already used elsewhere in this app, e.g.
 * `WorkspaceBomsTab.tsx`) is a DIFFERENT silhouette (a closed folder with a
 * front flap) — not a pixel match for this open-folder-with-tab shape — so
 * this is the design's own inline SVG, same hand-inlined-verbatim-path-data
 * convention as the icons above. Decorative alongside each call site's own
 * visible name text — always `aria-hidden`. */
export function IconFolderTile({ size = 16 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M3 7l0-1a2 2 0 0 1 2-2h4l2 3" />
    </svg>
  )
}

/** UID-317: the order-detail status stepper's step-to-step connector glyph —
 * the design's long right-arrow (`Fictiv Platform.dc.html:3899`,
 * `M5 12h14M13 6l6 6-6 6`), replacing the plain `›` text character
 * (`StatusStepper.tsx`). Purely decorative, always rendered `aria-hidden`
 * by the caller. */
export function IconArrowRight({ size = 16 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  )
}

/**
 * UID-382 — the "Card view" half of the Projects header's icon-only view-toggle
 * pill (`Fictiv Platform.dc.html:1607`, a 4-square grid glyph, 17×17px,
 * stroke-width 2). `IconCategoryGrid` above is the SAME four rects but carries
 * `rx="1.5"` (rounded corners, per the catalog group tile it was authored for);
 * the toggle-pill glyph in the comp has NO `rx` — sharp corners — so reusing it
 * would visibly diverge at this size rather than save a component. Hence the
 * comp's verbatim rect geometry, hand-inlined, same convention as the icons
 * above. `@ant-design/icons`'s `AppstoreOutlined` (already re-exported here) is
 * also a 4-square grid but a filled/rounded one, not this outline glyph.
 * Decorative — the interactive parent `<button>` carries the accessible name
 * ("Card view"), so this is always `aria-hidden`. */
export function IconCardView({ size = 17 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  )
}

/**
 * UID-382 — the "List view" half of the same toggle pill
 * (`Fictiv Platform.dc.html:1608`): the classic Lucide "list" glyph — three
 * long horizontal rules (`x1=8 → x2=21`) each preceded by a bullet expressed as
 * a zero-length line (`x1=3 → x2=3.01`, which `stroke-linecap="round"` renders
 * as a dot), 17×17px, stroke-width 2. `@ant-design/icons`'s `UnorderedListOutlined`
 * is the nearest equivalent but draws its bullets as filled squares on a
 * different baseline grid — not a match for this dot-and-rule shape — so this is
 * the comp's verbatim line geometry, hand-inlined. Decorative; the parent
 * `<button>` carries the accessible name ("List view"), so always `aria-hidden`. */
export function IconListView({ size = 17 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  )
}

/**
 * UID-373 — the trailing chevron on each Projects **list row**
 * (`Fictiv Platform.dc.html:1664`): a thin 2px-stroke caret, 17×17px, in
 * `--fg-3`. `@ant-design/icons`'s `RightOutlined` (already re-exported at the
 * top of this file) is NOT a match — it is a filled/solid caret triangle, not
 * a stroked chevron. That distinction is already established precedent in this
 * codebase rather than a new judgement call: `CartPage.tsx:1050` carries a
 * local `ChevronRightIcon` for exactly this reason ("design uses a stroke
 * caret, not the Ant filled `RightOutlined`"). This is the same glyph promoted
 * to a shared export, so a third caller has something to reuse.
 *
 * To be straight about the duplication that remains: `CartPage`'s local copy is
 * byte-identical in path data but renders at 18px, and migrating it
 * (`<IconChevronRight size={18} />`) would be a genuine one-liner with no
 * visual diff. It is left alone here purely to keep this change additive and
 * scoped to UID-373 — not because the two cannot be unified. That migration is
 * a clean follow-up for whoever next touches the cart's empty state.
 *
 * The comp draws it as
 * `<polyline points="9 18 15 12 9 6">`, written here as the equivalent `path`
 * form. Decorative — the interactive parent `<button>` carries the accessible
 * name, so this is always `aria-hidden`. */
export function IconChevronRight({
  size = 17,
  strokeWidth = 2,
}: {
  readonly size?: number
  readonly strokeWidth?: number
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  )
}

/**
 * UID-409 — the BOM detail page's "Back to library" chevron (`Fictiv
 * Platform.dc.html:1858`, `m15 18-6-6 6-6`, 15px / stroke 2). The mirror of
 * `IconChevronRight` above. `@ant-design/icons`'s `LeftOutlined` is a
 * filled/solid arrowhead, not the design's thin stroke-outline chevron —
 * same "prototype has no ant-design match" rationale as the icons above.
 * Decorative alongside the button's own visible "Back to library" text —
 * always `aria-hidden`. */
export function IconChevronLeft({
  size = 15,
  strokeWidth = 2,
}: {
  readonly size?: number
  readonly strokeWidth?: number
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  )
}

/**
 * UID-409 — the BOM detail header's folder glyph (`Fictiv Platform.dc.html:1860`,
 * the classic lucide "folder" `M4 20h16…`, rendered at 26px inside the 52×52
 * tinted tile). NOT `IconFolderTile` above — that is a different open-folder-
 * with-tab silhouette; this is the design's own closed-folder-with-notch shape.
 * `@ant-design/icons`'s `FolderOutlined` is a different silhouette again. Uses
 * the same verbatim-prototype-path convention as the icons above. Decorative —
 * always `aria-hidden`. */
export function IconFolder({ size = 26 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
    </svg>
  )
}

/**
 * UID-409 — the BOM detail header's "Share" glyph (`Fictiv Platform.dc.html:1899`,
 * the lucide "share-2" three-node graph, 16px / stroke 2). `@ant-design/icons`'s
 * `ShareAltOutlined` is a filled/solid glyph, not this design's stroke-outline
 * silhouette — same rationale as `IconCartGlyph` above. Decorative alongside the
 * button's own visible "Share" text — always `aria-hidden`. */
export function IconShare({ size = 16 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
      <line x1="15.4" y1="6.5" x2="8.6" y2="10.5" />
    </svg>
  )
}

/**
 * UID-409 — the BOM detail header's "Delete" glyph (`Fictiv Platform.dc.html:1900`,
 * the lucide "trash-2", 16px / stroke 2). `@ant-design/icons`'s `DeleteOutlined`
 * is a filled/solid glyph, not this design's stroke-outline silhouette — same
 * rationale as `IconCartGlyph` above. Decorative alongside the button's own
 * visible "Delete" text — always `aria-hidden`. */
export function IconTrash({ size = 16 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  )
}

/**
 * UID-433 — the Parts-tab Upload dropdown's "Copy from Excel" item
 * (`Fictiv Platform.dc.html:9959`, `wsUploadOptions[1].iconPath`). Path data
 * lifted from the design export's document-with-an-"X" glyph — not an
 * ant-design/lucide substitute. The comp's ONE `iconPath` string is split
 * across two `<path>` elements here (an outline path + the fold/"X" detail
 * lines); rendering is identical since both share `fill="none"`, this is not
 * a content change. Decorative alongside the item's own visible title/sub
 * text — always `aria-hidden`. */
export function IconFileX({ size = 17 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M9 13l6 6M15 13l-6 6" />
    </svg>
  )
}

/**
 * UID-433 — the Parts-tab Upload dropdown's "Add by part number" item
 * (`Fictiv Platform.dc.html:9960`, `wsUploadOptions[2].iconPath`). Exact path
 * data lifted verbatim from the design export, a search glyph with a "+"
 * crossbar — not the plain magnifying-glass `Icon name="search"` used
 * elsewhere, which doesn't carry the "+" and would be a visible mismatch here.
 * Decorative alongside the item's own visible title/sub text — always
 * `aria-hidden`. */
export function IconSearchPlus({ size = 17 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M11 8v6M8 11h6M21 21l-4.3-4.3M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z" />
    </svg>
  )
}
